/**
 * The API reads the tenant from an `incoming-origin` header (a query param on WebSockets), and every
 * web bundle derives it from its hostname (`<tenant>.admin.<domain>`). In the app the page is served
 * from `localhost`, so a bundle that predates the native workspace sends an empty tenant and the API
 * answers 404 "Organisation not found" to every call. The stock microfrontends are exactly that kind
 * of bundle: they are deployed on their own and loaded from their CDN at runtime, so an installed app
 * meets whatever version is live — older or newer than itself.
 *
 * In the app the shell owns the workspace, so it stamps it onto every request that already carries
 * the header / param, whichever bundle made it. Requests without one (third-party APIs) are left
 * untouched — adding a custom header there would trigger a CORS preflight they reject. Nothing is
 * stamped while no workspace is stored, which keeps the workspace screen's own check of a typed
 * tenant intact (that screen only shows when none is stored).
 */
import { getStoredTenant } from './platform';

const TENANT_FIELD = 'incoming-origin';

/** axios (and anything else on XHR) — the path every microfrontend API call takes. */
function patchXhr() {
  const proto = window.XMLHttpRequest?.prototype;
  if (!proto?.setRequestHeader) return;
  const setRequestHeader = proto.setRequestHeader;
  proto.setRequestHeader = function setTenantAwareRequestHeader(name, value) {
    const tenant = String(name).toLowerCase() === TENANT_FIELD ? getStoredTenant() : '';
    return setRequestHeader.call(this, name, tenant || value);
  };
}

function patchFetch() {
  const nativeFetch = window.fetch;
  if (typeof nativeFetch !== 'function') return;
  window.fetch = function tenantAwareFetch(input, init) {
    const tenant = getStoredTenant();
    if (tenant) {
      // `init.headers`, when given, replaces the Request's own headers entirely.
      if (init?.headers) {
        const headers = new Headers(init.headers);
        if (headers.has(TENANT_FIELD)) {
          headers.set(TENANT_FIELD, tenant);
          init = { ...init, headers };
        }
      } else if (input instanceof Request && input.headers.has(TENANT_FIELD)) {
        const headers = new Headers(input.headers);
        headers.set(TENANT_FIELD, tenant);
        input = new Request(input, { headers });
      }
    }
    return nativeFetch.call(this, input, init);
  };
}

/** Browsers can't put headers on a WebSocket, so the tenant rides in the URL (e.g. the stock-count lock). */
function patchWebSocket() {
  const NativeWebSocket = window.WebSocket;
  if (typeof NativeWebSocket !== 'function') return;
  const withTenant = (url) => {
    const tenant = getStoredTenant();
    if (!tenant) return url;
    try {
      const parsed = new URL(url, window.location.href);
      if (!parsed.searchParams.has(TENANT_FIELD)) return url;
      parsed.searchParams.set(TENANT_FIELD, tenant);
      return parsed.toString();
    } catch (e) {
      return url;
    }
  };
  // A subclass keeps `instanceof WebSocket` and the static OPEN / CLOSED constants working.
  window.WebSocket = class TenantAwareWebSocket extends NativeWebSocket {
    constructor(url, protocols) {
      super(withTenant(url), protocols);
    }
  };
}

let installed = false;

export function installTenantRequests() {
  if (installed) return;
  installed = true;
  patchXhr();
  patchFetch();
  patchWebSocket();
}
